import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EDatePicker from '@/components/everridge/EDatePicker';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import { useTenantsSelectOptionsApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsSelectOptionsApi';
import {
    SuperAdminDataPageFilterFormSchema,
    SuperAdminDataPageFilterFormData,
} from '@/features/superAdmin/features/data/shared/validators/SuperAdminDataPageFilterValidator';
import {
    useSuperAdminDataApartmentsLookupApi,
    useSuperAdminDataHotelsLookupApi,
    useSuperAdminDataOwnersLookupApi,
} from '@/features/superAdmin/features/data/shared/hooks/useSuperAdminDataLookupsApi';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SuperAdminDataListFilterState } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    onClose: () => void;
    isMobile?: boolean;
    showDocumentNumber?: boolean;
    showHotelFilter?: boolean;
    filter: SuperAdminDataListFilterState;
    setFilter: (filter: Partial<SuperAdminDataListFilterState>) => void;
    resetFilter: () => void;
};

const SuperAdminDataPageFilter: FC<Props> = ({
    onClose,
    isMobile,
    showDocumentNumber = false,
    showHotelFilter = false,
    filter,
    setFilter,
    resetFilter,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();
    const { tenantsSelectData } = useTenantsSelectOptionsApi();

    const { control, handleSubmit, reset, watch, setValue, register } =
        useForm<SuperAdminDataPageFilterFormData>({
            resolver: zodResolver(SuperAdminDataPageFilterFormSchema),
            values: {
                startDate: filter.startDate,
                endDate: filter.endDate,
                tenantId: filter.tenantId,
                hotelId: filter.hotelId,
                ownerId: filter.ownerId,
                apartmentId: filter.apartmentId,
                documentNumber: filter.documentNumber,
            },
        });

    const tenantIdValue = watch('tenantId');
    const hotelIdValue = watch('hotelId');
    const ownerIdValue = watch('ownerId');
    const apartmentIdValue = watch('apartmentId');

    const { data: ownersData } = useSuperAdminDataOwnersLookupApi(tenantIdValue);
    const { data: hotelsData } = useSuperAdminDataHotelsLookupApi(
        showHotelFilter ? tenantIdValue : null,
    );
    const { data: apartmentsData } = useSuperAdminDataApartmentsLookupApi(
        tenantIdValue,
        undefined,
        showHotelFilter ? hotelIdValue : undefined,
    );

    const tenantSelectOptions = useMemo(
        () =>
            (tenantsSelectData?.items ?? []).map((tenant) => ({
                value: tenant.id,
                label: tenant.name ?? tenant.id,
            })),
        [tenantsSelectData?.items],
    );

    const hotelSelectOptions = useMemo(
        () =>
            (hotelsData?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [hotelsData?.items],
    );

    const ownerSelectOptions = useMemo(
        () =>
            (ownersData?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [ownersData?.items],
    );

    const apartmentSelectOptions = useMemo(
        () =>
            (apartmentsData?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [apartmentsData?.items],
    );

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const onSubmit = (data: SuperAdminDataPageFilterFormData) => {
        setFilter({
            ...data,
            hotelId: showHotelFilter ? data.hotelId : null,
            skip: 0,
        });
        handleClose();
    };

    const handleReset = () => {
        resetFilter();
        reset({
            startDate: null,
            endDate: null,
            tenantId: null,
            hotelId: null,
            ownerId: null,
            apartmentId: null,
            documentNumber: null,
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className={cn(isMobile && 'max-h-[90vh] overflow-y-auto')}>
                <DialogHeader>
                    <DialogTitle>{t('superAdmin.data.filters.title')}</DialogTitle>
                    <DialogDescription>{t('superAdmin.data.filters.description')}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label>{t('superAdmin.data.filters.tenant')}</Label>
                        <ESingleSelectPicker
                            value={tenantIdValue}
                            onChange={(value) => {
                                setValue('tenantId', value);
                                setValue('hotelId', null);
                                setValue('ownerId', null);
                                setValue('apartmentId', null);
                            }}
                            options={tenantSelectOptions}
                            placeholder={t('superAdmin.data.filters.tenantPlaceholder')}
                            allOptionLabel={t('superAdmin.data.filters.allTenants')}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <EDatePicker
                            control={control}
                            fieldName="startDate"
                            label={t('superAdmin.data.filters.startDate')}
                        />
                        <EDatePicker
                            control={control}
                            fieldName="endDate"
                            label={t('superAdmin.data.filters.endDate')}
                        />
                    </div>
                    {showDocumentNumber && (
                        <div className="space-y-2">
                            <Label htmlFor="documentNumber">{t('superAdmin.data.filters.documentNumber')}</Label>
                            <Input id="documentNumber" {...register('documentNumber')} />
                        </div>
                    )}
                    {showHotelFilter && (
                        <div className="flex flex-col gap-2">
                            <Label>{t('superAdmin.data.filters.hotel')}</Label>
                            <ESingleSelectPicker
                                value={hotelIdValue}
                                onChange={(value) => {
                                    setValue('hotelId', value);
                                    setValue('apartmentId', null);
                                }}
                                options={hotelSelectOptions}
                                placeholder={t('superAdmin.data.filters.hotelPlaceholder')}
                                allOptionLabel={t('superAdmin.data.filters.allHotels')}
                                disabled={!tenantIdValue}
                            />
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <Label>{t('superAdmin.data.filters.owner')}</Label>
                        <ESingleSelectPicker
                            value={ownerIdValue}
                            onChange={(value) => setValue('ownerId', value)}
                            options={ownerSelectOptions}
                            placeholder={t('superAdmin.data.filters.ownerPlaceholder')}
                            allOptionLabel={t('superAdmin.data.filters.allOwners')}
                            disabled={!tenantIdValue}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label>{t('superAdmin.data.filters.room')}</Label>
                        <ESingleSelectPicker
                            value={apartmentIdValue}
                            onChange={(value) => setValue('apartmentId', value)}
                            options={apartmentSelectOptions}
                            placeholder={t('superAdmin.data.filters.roomPlaceholder')}
                            allOptionLabel={t('superAdmin.data.filters.allRooms')}
                            disabled={!tenantIdValue}
                        />
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={handleReset}>
                            {t('elements.buttons.reset')}
                        </Button>
                        <DialogClose asChild>
                            <Button type="button" variant="outline" className="gap-2">
                                <Cancel_01_Icon className="h-4 w-4" />
                                {t('elements.buttons.cancel')}
                            </Button>
                        </DialogClose>
                        <Button type="submit">{t('elements.buttons.apply')}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default SuperAdminDataPageFilter;
